import { useEffect, useState } from "react";
import { BookOpen, ChevronDown, Gift, Search } from "lucide-react";
import { WhatsAppGlyph } from "@/components/cards/CardShell";
import { waLink } from "@/lib/wa";
import { STOCK, resell, catalogUrl, prodQty, campCount, campMissing, campPrice, campRule, fromPrices, giftText, imgUrl, logo, per60, realFactor, ron, vol } from "@/lib/bf";

const ResellLine = ({ p, c }) => {
  const r = resell(p.salon, c);
  return r && r.win > 0 ? (
    <small className="bf-resell" data-testid={`bf-resell-${c.id}-${p.id}`}>Cumperi cu <b>-{c.discount}%</b> · vinzi cu <b>-{c.resell_discount}%</b> față de online · câștigi <b className="is-win">{r.pct}%</b> din vânzare</small>
  ) : null;
};

const Row = ({ p, c, qty, setQty, dye }) => {
  const key = `cp:${c.id}:${p.id}`;
  return (
    <li className={`bf-camp-row ${p.line_color ? "has-line" : ""}`} style={p.line_color ? { "--line": p.line_color } : undefined} data-testid={`bf-camp-row-${c.id}-${p.id}`}>
      <div className={`bf-camp-thumb ${p.image ? "" : "is-empty"}`}><img src={p.image ? imgUrl(p.image) : logo(p.brand)} alt="" loading="lazy" /></div>
      <div className="bf-camp-info">
        <span>{p.code && <em className="bf-code" data-testid={`bf-code-${p.id}`}>{p.code}</em>}{p.name}{p.units > 1 ? ` · contează ${p.units} vopsele` : ""}</span>
        {p.desc && <small className="bf-desc" data-testid={`bf-desc-${p.id}`}>{p.desc}</small>}
        <div>{p.salon > 0 ? <><b data-testid={`bf-camp-price-${c.id}-${p.id}`}>{ron(campPrice(p, c))}</b> <s>{ron(p.salon)}</s></> : <b data-testid={`bf-camp-price-${c.id}-${p.id}`}>Preț în curând</b>}{p.retail > 0 && <em> client final {ron(p.retail)}</em>}</div>
        <ResellLine p={p} c={c} />
        {dye && per60(p, c) > 0 && <small className="bf-per60" data-testid={`bf-camp-per60-${c.id}-${p.id}`}>{`60 ml = ${ron(per60(p, c))}`}</small>}
      </div>
      <div className="bf-cart-q">
        <button type="button" onClick={() => setQty(key, (qty || 0) - 1)} aria-label="Scade" data-testid={`bf-camp-minus-${c.id}-${p.id}`}>−</button>
        <b>{qty || 0}</b>
        <button type="button" onClick={() => setQty(key, (qty || 0) + 1)} aria-label="Adaugă" data-testid={`bf-camp-plus-${c.id}-${p.id}`}>+</button>
      </div>
    </li>
  );
};

export const CatalogLink = ({ cat, small }) => cat && (
  <a className={`bf-catalog-link ${small ? "is-small" : ""}`} href={catalogUrl(cat.id)} target="_blank" rel="noopener noreferrer" data-testid={`bf-catalog-${cat.id}`}>
    <BookOpen size={small ? 13 : 16} /> {small ? "Catalog nuanțe" : cat.title}
  </a>
);

const ShadeRow = ({ p, c, cart, setQty, dye, catalog, byId, hint = "" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    const h = hint.trim().toLowerCase();
    if (h && !p.name.toLowerCase().includes(h) && p.shades.some((s) => `${s.code} ${s.name}`.toLowerCase().includes(h))) { setOpen(true); setQ(hint.trim()); }
    else setQ("");
  }, [hint, p]);
  const total = prodQty(c, p.id, cart, byId);
  const grouped = p.shades.length > new Set(p.shades.map((s) => s.name)).size * 1.5;
  const list = p.shades.filter((s) => !q || `${s.code} ${s.name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <li className="bf-shade-row" data-testid={`bf-shade-row-${c.id}-${p.id}`}>
      <div className={`bf-camp-row ${p.line_color ? "has-line" : ""}`} style={p.line_color ? { "--line": p.line_color } : undefined}>
        <div className={`bf-camp-thumb ${p.image ? "" : "is-empty"}`}><img src={p.image ? imgUrl(p.image) : logo(p.brand)} alt="" loading="lazy" /></div>
        <div className="bf-camp-info">
          <span>{p.name}{p.units > 1 ? ` · contează ${p.units} vopsele` : ""}</span>
        {p.desc && <small className="bf-desc" data-testid={`bf-desc-${p.id}`}>{p.desc}</small>}
          <div><b>{ron(campPrice(p, c))}</b> <s>{ron(p.salon)}</s></div>
          {dye && per60(p, c) > 0 && <small className="bf-per60">{`60 ml = ${ron(per60(p, c))}`}</small>}
        </div>
        <button type="button" className={`bf-shade-btn ${total ? "has" : ""}`} onClick={() => setOpen(!open)} data-testid={`bf-shade-open-${c.id}-${p.id}`}>
          {total ? `${total} buc.` : "Nuanțe"} <ChevronDown size={14} />
        </button>
      </div>
      {open && (
        <div className="bf-shades">
          <div className="bf-shades-bar">
            <label><Search size={13} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută cod sau nuanță" data-testid={`bf-shade-search-${c.id}-${p.id}`} /></label>
            <CatalogLink cat={catalog} small />
          </div>
          <ul>
            {list.map((s, i) => {
              const head = grouped && !q && s.name && s.name !== list[i - 1]?.name;
              const key = `cp:${c.id}:${p.id}:${s.code}`;
              const n = cart[key] || 0;
              return (
                <li key={s.code} className={`${s.out ? "is-out" : n ? "is-on" : ""} ${head ? "has-head" : ""}`} data-head={head ? s.name : undefined} data-testid={`bf-shade-${c.id}-${p.id}-${s.code}`}>
                  <b>{s.code}</b><span>{q || !grouped ? s.name : ""}{s.out ? " · stoc epuizat" : ""}</span>
                  {!s.out && (
                    <div className="bf-cart-q">
                      <button type="button" onClick={() => setQty(key, n - 1)} aria-label="Scade" data-testid={`bf-shade-minus-${c.id}-${p.id}-${s.code}`}>−</button>
                      <b>{n}</b>
                      <button type="button" onClick={() => setQty(key, n + 1)} aria-label="Adaugă" data-testid={`bf-shade-plus-${c.id}-${p.id}-${s.code}`}>+</button>
                    </div>
                  )}
                </li>
              );
            })}
            {!list.length && <li className="bf-camp-hint">Nicio nuanță găsită.</li>}
          </ul>
        </div>
      )}
    </li>
  );
};

export const List = ({ ids, c, byId, cart, setQty, dye, catalogs = [], hint = "" }) => (
  <ul>{ids.filter((id) => byId[id]).map((id) => {
    const p = byId[id];
    if (!p.shades?.length) return <Row key={id} p={p} c={c} dye={dye} qty={cart[`cp:${c.id}:${id}`]} setQty={setQty} />;
    const catalog = catalogs.find((x) => x.brand === p.brand && x.group === p.group) || catalogs.find((x) => x.brand === p.brand && !x.group);
    return <ShadeRow key={id} p={p} c={c} dye={dye} cart={cart} setQty={setQty} catalog={catalog} byId={byId} hint={hint} />;
  })}</ul>
);

const r2 = (v) => Math.round(v * 100) / 100;

const wantMsg = (c, brandName, contact) => `Bună ziua${contact.name ? `, ${contact.name}` : ""}! Vreau oferta Black Friday ${brandName} – ${c.name}: ${campRule(c)}, discount -${c.discount}%${c.gift ? `, bonus gratuit ${giftText(c)}` : ""}. Salon: `;

export const PackCard = ({ c, byId, cart, setQty, i, contact, onWant, brandName, catalogs }) => {
  const n = campCount(c, cart, byId);
  const left = campMissing(c, n);
  const extras = c.extras || [];
  const [open, setOpen] = useState(n.pieces + n.ox > 0);
  return (
    <article className="bf-camp bf-pack-v2" style={{ animationDelay: `${i * 90}ms` }} data-testid={`bf-camp-${c.id}`}>
      <header>
        <h4>{c.name}</h4>
        <span className="bf-badge bf-badge--inline">-{c.discount}%</span>
      </header>
      <p className="bf-stock" data-testid={`bf-camp-stock-${c.id}`}>{STOCK}</p>
      <ul className="bf-pack-what" data-testid={`bf-camp-what-${c.id}`}>
        <li>{campRule(c)}</li>
        <li>Discount financiar <b>-{c.discount}%</b> la toate produsele din pachet</li>
        {c.gift && <li className="bf-camp-gift" data-testid={`bf-camp-gift-${c.id}`}><Gift size={13} /> Bonus gratuit: {giftText(c)}</li>}
      </ul>
      <p className="bf-camp-rule" data-testid={`bf-camp-min-${c.id}`}>{n.pieces + n.ox > 0 ? (left.length ? `Mai ai ${left.join(" și ")}` : "Pachet complet ✓") : "Alege produsele ca să completezi pachetul"}</p>
      {contact?.phone && (
        <a className="bf-want" href={waLink(contact.phone, wantMsg(c, brandName, contact))} target="_blank" rel="noopener noreferrer" onClick={() => onWant?.(c.id)} data-testid={`bf-want-${c.id}`}>
          <WhatsAppGlyph /> VREAU
        </a>
      )}
      {c.items.length ? (
        <button type="button" className={`bf-pack-open ${open ? "is-open" : ""}`} onClick={() => setOpen(!open)} data-testid={`bf-camp-open-${c.id}`}>
          {open ? "Ascunde produsele" : "Alege produsele"} <ChevronDown size={16} />
        </button>
      ) : <p className="bf-camp-empty" data-testid={`bf-camp-empty-${c.id}`}>Produsele pentru acest pachet se adaugă în curând.</p>}
      {open && c.items.length > 0 && (
        <div className="bf-pack-lists">
          <p className="bf-camp-sub">{c.category === "culoare" ? "Vopsele" : "Produse"} la alegere</p>
          <List ids={c.items} c={c} byId={byId} cart={cart} setQty={setQty} dye={c.category === "culoare"} catalogs={catalogs} />
          {extras.length > 0 && (
            <>
              <p className="bf-camp-sub">Oxidanți la alegere{c.extra_qty > 0 ? ` (${c.extra_qty} în pachet)` : ""}</p>
              <List ids={extras} c={c} byId={byId} cart={cart} setQty={setQty} />
            </>
          )}
        </div>
      )}
    </article>
  );
};

const minOver = (camps, byId, key) => {
  const m = {};
  camps.forEach((c) => {
    const f = realFactor(c)[key === "items" ? "dye" : "ox"];
    fromPrices(c, byId, key === "items" ? c.items : c.extras || []).forEach(([g, v, p]) => {
      const cur = m[g] || { v, p, c, real: Infinity };
      if (v < cur.v) Object.assign(cur, { v, p, c });
      if (f < 1 && v * f < cur.real) Object.assign(cur, { real: r2(v * f), real60: r2(per60(p, c) * f) });
      m[g] = cur;
    });
  });
  return Object.entries(m);
};

const GAMA = {
  "Luxviva": ["#b0245f", "luxviva"],
  "Choice Color": ["#8fd3bf", "cc"],
  "Choice Elite": ["#c9d3dc", "elite"],
  "CNC": ["#a3474f", "cnc"],
  "CNC Root": ["#8a3a3c", "cncroot"],
};

const MinTile = ({ g, v, p, c, real, real60, id, dye, i }) => {
  const [flip, setFlip] = useState(false);
  const t = `${id}-${g.replace(/\s+/g, "-").toLowerCase()}`;
  const [col, img] = GAMA[g] || [p.line_color || "#d4ae62", ""];
  const pic = img ? `/p-${id === "dyes" ? "dye" : "ox"}-${img}.png` : p.image ? imgUrl(p.image) : "";
  const soon = !(v > 0);
  return (
    <button type="button" className={`bf-flip ${flip ? "is-flipped" : ""}`} style={{ "--line": col, animationDelay: `${i * 80}ms` }} onClick={() => setFlip(!flip)} data-testid={`bf-min-tile-${t}`}>
      <div className="bf-flip-inner">
        <div className="bf-flip-front" data-testid={`bf-min-front-${t}`}>
          <div className="bf-flip-frame">{pic ? <img src={pic} alt={g} loading="lazy" /> : <i className="bf-flip-drop" />}</div>
          <span>{g}</span>
          {id === "dyes" && <small className="bf-flip-desc">{p.desc?.split(" · ")[0]}</small>}
          <em className="bf-flip-hint">Vezi prețul</em>
        </div>
        <div className="bf-min-tile has-line bf-flip-back">
          <span>{g}</span>
          {id === "dyes" && p.desc && <small className="bf-desc">{dye ? p.desc.split(" · ")[0] : p.desc}</small>}
          {vol(p) && <em data-testid={`bf-min-vol-${t}`}>{vol(p)}{p.units > 1 ? ` · preț pe flacon ${p.ml} ml` : ""}</em>}
          {soon ? <b className="bf-min-soon" data-testid={`bf-min-${t}`}>Preț în curând</b> : <><small>de la</small><b data-testid={`bf-min-${t}`}>{ron(v)}</b></>}
          {dye && per60(p, c) > 0 && <strong className="bf-per60" data-testid={`bf-min-per60-${t}`}>{`60 ml = ${ron(per60(p, c))}`}</strong>}
          {!soon && real < Infinity && (
            <div className="bf-min-real" data-testid={`bf-min-real-${t}`}>
              <small>Preț real cu extra bonus inclus</small>
              <b>{`de la ${ron(real)}`}</b>
              {dye && real60 > 0 && <strong className="bf-per60">{`60 ml = ${ron(real60)}`}</strong>}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

const MinBlock = ({ title, rows, note, id, dye }) => rows.length > 0 && (
  <div className="bf-min-block" data-testid={`bf-min-${id}`}>
    <h4>{title}</h4>
    <div className="bf-min-grid">
      {rows.map(([g, r], i) => <MinTile key={g} g={g} {...r} id={id} dye={dye} i={i} />)}
    </div>
    {note && <p className="bf-camp-hint">{note}</p>}
  </div>
);

export const MinPrices = ({ camps, byId, cat }) => {
  const dyes = minOver(camps, byId, "items");
  const ox = minOver(camps, byId, "extras");
  const liq = camps.some((c) => c.items.some((id) => byId[id]?.units > 1));
  if (!dyes.length && !ox.length) return null;
  return (
    <section className="bf-min bf-min-panel" data-testid="bf-min-prices">
      <header className="bf-min-head">
        <h3 data-testid="bf-min-title">{cat === "culoare" ? "Preț minim posibil vopsea" : cat === "decolorare" ? "Preț minim posibil decolorare" : "Preț minim posibil"}</h3>
        <p>{cat === "culoare" ? "(Află cu cât cumperi vopseaua în campanie)" : "(Află cu cât cumperi produsele în campanie)"}</p>
      </header>
      <MinBlock id="dyes" title={cat === "culoare" ? "Vopsele" : cat === "decolorare" ? "Decoloranți" : "Produse"} rows={dyes} dye={cat === "culoare"} note={liq ? "Vopseaua lichidă: preț pe flacon de 60 ml (cutia are 3 flacoane)." : ""} />
      <MinBlock id="ox" title="Oxidanți" rows={ox} />
    </section>
  );
};
